import { useState, useEffect, useCallback, useRef } from 'react';
import { AxiosError } from 'axios';

interface UseApiReturn<T> {
  data: T | undefined;
  loading: boolean;
  error: string | undefined;
  refetch: () => void;
}

function extractErrorMessage(error: unknown): string {
  const axiosErr = error as AxiosError<{ error?: string; message?: string }>;
  if (axiosErr?.response?.data) {
    return (
      axiosErr.response.data.error ||
      axiosErr.response.data.message ||
      'An unexpected error occurred. Please try again.'
    );
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'An unexpected error occurred. Please try again.';
}

// Simple in-memory cache that survives unmount/remount within the same session.
// No TTL or invalidation — just keeps the last successful fetch result so
// navigating away and back shows data instantly while a background refresh runs.
const apiCache = new Map<string, unknown>();

export function useApi<T>(
  fetcher: () => Promise<T>,
  deps: any[] = [],
  cacheKey?: string
): UseApiReturn<T> {
  const cached = cacheKey ? (apiCache.get(cacheKey) as T | undefined) : undefined;
  const [data, setData] = useState<T | undefined>(cached);
  const [loading, setLoading] = useState(cached === undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const [fetchCount, setFetchCount] = useState(0);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const refetch = useCallback(() => {
    setFetchCount((c) => c + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const hasCachedData = cacheKey != null && apiCache.has(cacheKey);

    // Only show loading spinner when there's no cached data to display
    if (!hasCachedData) {
      setLoading(true);
    }
    setError(undefined);

    fetcherRef
      .current()
      .then((result) => {
        if (!cancelled) {
          setData(result);
          if (cacheKey) {
            apiCache.set(cacheKey, result);
          }
        }
      })
      .catch((err) => {
        if (!cancelled) {
          // If we have cached data on screen, swallow the error silently —
          // the user still sees usable (slightly stale) data.
          // Only surface the error when there's nothing to show.
          if (!hasCachedData) {
            setError(extractErrorMessage(err));
          }
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchCount, ...deps]);

  return { data, loading, error, refetch };
}
